import { Input, Select, Space } from 'antd';
import {
    Calculator,
    CaretDown,
    CarSimple,
    GasPump,
    MapPin,
    Money,
    Path
} from 'phosphor-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GoogleMapsLoader from '../components/GoogleMapsLoader';
import LocationAutocomplete from '../components/LocationAutocomplete';
import TrackerLayout from '../components/ui/TrackerLayout';
import { ToolsPageContentKey } from '../constants';
import Layout from '../layout/Layout';
import {
    getFuelPriceTracker,
    getToolPageContent,
    IToolsPageContent
} from '../redux/actions/trackerActions';
import {
    appendCurrency,
    contentAccordToCurrentLang,
    SetMeta
} from '../utils/Helper/jsHelpers';
import {
    getCitizenDemonym,
    getCountryNameFromDomain
} from '../utils/translation';

type FuelPrice = {
    fuelType: string;
    inlandPrice: number;
    coastalPrice: number;
    _id: string;
};

type FuelPriceData = {
    _id: string;
    fuelPrices: FuelPrice[];
    effectiveDate: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
    status: boolean;
};

const DrivingCostCalculator = () => {
    const defaultCurrency = process.env.REACT_APP_DEFAULT_CURRENCY;

    const [distanceUnit, setDistanceUnit] = useState('km');
    const [fuelType, setFuelType] = useState('');
    const [fuelPrice, setFuelPrice] = useState(0);
    const [fuelPriceUnit, setFuelPriceUnit] = useState(
        `${defaultCurrency}/ltr`
    );
    const [fuelEconomyUnit, setFuelEconomyUnit] = useState('ltr/100km');
    const [distance, setDistance] = useState(0);
    const [fuelEconomy, setFuelEconomy] = useState(20);
    const [calculatedPrice, setCalculatedPrice] = useState(0);
    const [toolContentData, setToolContentData] =
        useState<null | IToolsPageContent>(null);
    const [fuelData, setFuelData] = useState<FuelPriceData | null>(null);

    // New states for Google Maps integration
    const [startAddress, setStartAddress] = useState('');
    const [endAddress, setEndAddress] = useState('');
    const [isManualDistance, setIsManualDistance] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    const { t } = useTranslation();

    // Fetch fuel price data
    useEffect(() => {
        getFuelPriceTracker()
            .then((fuelPrices: any) => {
                if (
                    fuelPrices.data &&
                    fuelPrices.data.fuelPrices &&
                    fuelPrices.data.fuelPrices.length > 0
                ) {
                    setFuelData(fuelPrices.data);
                    // Set default fuel type and price from first item in API
                    const firstFuel = fuelPrices.data.fuelPrices[0];
                    setFuelType(firstFuel.fuelType);
                    setFuelPrice(firstFuel.inlandPrice || 0);
                }
            })
            .catch(() => {
                // Set fallback values if API fails
                setFuelType('petrol');
                setFuelPrice(13.53);
            });
    }, []);

    // Update fuel price when fuel type changes
    useEffect(() => {
        if (fuelData?.fuelPrices) {
            const selectedFuel = fuelData.fuelPrices.find(
                (fp: FuelPrice) => fp.fuelType === fuelType
            );
            if (selectedFuel?.inlandPrice) {
                setFuelPrice(selectedFuel.inlandPrice);
            }
        }
    }, [fuelType, fuelData]);

    useEffect(() => {
        if (distanceUnit === 'km') {
            setFuelEconomyUnit('ltr/100km');
        }
        if (distanceUnit === 'mi') {
            setFuelEconomyUnit('mpg');
        }
    }, [distanceUnit]);

    const calculateDistance = useCallback(
        async (origin: string, destination: string) => {
            if (
                !origin ||
                !destination ||
                typeof window === 'undefined' ||
                !window.google
            ) {
                return 0;
            }

            try {
                const directionsService =
                    new window.google.maps.DirectionsService();
                const result = await directionsService.route({
                    origin: origin,
                    destination: destination,
                    travelMode: window.google.maps.TravelMode.DRIVING
                });

                if (result.routes && result.routes[0]) {
                    const route = result.routes[0];
                    const distanceInMeters = route.legs[0].distance?.value || 0;
                    const distanceInKm = distanceInMeters / 1000;
                    return Math.round(distanceInKm * 100) / 100;
                }
            } catch {
                return 0;
            }
            return 0;
        },
        []
    );

    useEffect(() => {
        if (
            startAddress &&
            endAddress &&
            !isManualDistance &&
            startAddress.trim().length > 10 &&
            endAddress.trim().length > 10 &&
            startAddress !== endAddress
        ) {
            calculateDistance(startAddress, endAddress).then(
                (calculatedDistance) => {
                    if (calculatedDistance > 0) {
                        setDistance(calculatedDistance);
                    }
                }
            );
        }
    }, [startAddress, endAddress, calculateDistance, isManualDistance]);

    const handleManualDistanceChange = (value: number) => {
        setDistance(value);
        setIsManualDistance(true);
        if (value > 0) {
            setStartAddress('');
            setEndAddress('');
        }
    };

    const handleAddressChange = (type: 'start' | 'end', value: string) => {
        if (type === 'start') {
            setStartAddress(value);
        } else {
            setEndAddress(value);
        }
        if (value) {
            setIsManualDistance(false);
        }
    };

    const calculateDrivingCost = useCallback(() => {
        if (!distance || !fuelPrice || !fuelEconomy) {
            setCalculatedPrice(0);
            return;
        }

        let calculatedCost: number;
        let convertedDistance: number;
        let convertedFuelEconomy: number;
        let convertedFuelPrice: number;

        // Convert distance to kilometers (keep as is since we're working in km)
        convertedDistance = distance;

        // Convert fuel economy to L/100km
        if (fuelEconomyUnit === 'mpg') {
            convertedFuelEconomy = 235.214 / fuelEconomy; // Convert MPG to L/100km
        } else {
            convertedFuelEconomy = fuelEconomy;
        }
        // Convert fuel price to $/L
        convertedFuelPrice = fuelPriceUnit.includes('gallon')
            ? fuelPrice / 3.78541
            : fuelPrice;

        // Calculate cost: (distance * fuel economy / 100) * fuel price
        calculatedCost =
            ((convertedDistance * convertedFuelEconomy) / 100) *
            convertedFuelPrice;
        calculatedCost = Math.round(calculatedCost * 100) / 100;

        setCalculatedPrice(calculatedCost);
    }, [
        distance,
        fuelPrice,
        fuelEconomy,
        distanceUnit,
        fuelEconomyUnit,
        fuelPriceUnit
    ]);

    useEffect(() => {
        calculateDrivingCost();
    }, [calculateDrivingCost]);

    const formatCalculatedPrice = () => {
        return calculatedPrice < 0 ? (
            <span>{t('enterAllValues')}</span>
        ) : (
            <h3>{appendCurrency(calculatedPrice)}</h3>
        );
    };

    useEffect(() => {
        getToolPageContent(ToolsPageContentKey.drivingCostCalculator)
            .then((res: any) => {
                if (res.status === 200) setToolContentData(res.data.data);
            })
            .catch(() => {});
    }, []);

    const contentData = () => {
        if (!toolContentData) return <></>;
        return (
            <div className="tracker-content">
                <div
                    className="tracker-content-data"
                    dangerouslySetInnerHTML={{
                        __html: contentAccordToCurrentLang(toolContentData)
                            .description
                    }}
                />
            </div>
        );
    };

    const converterNode = () => {
        const { Option } = Select;

        const selectAfter = (
            <Select
                value={distanceUnit}
                className="select-after"
                popupClassName="calculator-options"
                onChange={(value) => setDistanceUnit(value)}
                suffixIcon={
                    <CaretDown size={24} color="#454545" weight="bold" />
                }
            >
                <Option value="mi">mi</Option>
                <Option value="km">km</Option>
            </Select>
        );

        const selectAfterFuelPriceUnit = (
            <Select
                value={fuelPriceUnit}
                className="select-after"
                popupClassName="calculator-options"
                onChange={(value) => setFuelPriceUnit(value)}
                suffixIcon={
                    <CaretDown size={24} color="#454545" weight="bold" />
                }
            >
                <Option
                    value={`${defaultCurrency}/ltr`}
                >{`${defaultCurrency}/ltr`}</Option>
                <Option
                    value={`${defaultCurrency}/gallon`}
                >{`${defaultCurrency}/gallon`}</Option>
            </Select>
        );

        const selectAfterFuelEconomyUnit = (
            <Select
                value={fuelEconomyUnit}
                className="select-after"
                popupClassName="calculator-options"
                onChange={(value) => setFuelEconomyUnit(value)}
                suffixIcon={
                    <CaretDown size={24} color="#454545" weight="bold" />
                }
            >
                <Option value="ltr/100km">ltr/100km</Option>
                <Option value="mpg">MPG</Option>
            </Select>
        );

        return (
            <div className="calculator-ui-container">
                <div className="calculator-ui">
                    <div className="calc-form" style={{ width: '100%' }}>
                        {/* Address Inputs Section */}
                        <div className="calc-form-row">
                            <LocationAutocomplete
                                label={t('startAddress')}
                                placeholder={t('startAddressPlaceholder')}
                                value={startAddress}
                                onChange={(value) =>
                                    handleAddressChange('start', value)
                                }
                                useGoogleMaps={true}
                            />
                            <LocationAutocomplete
                                label={t('endAddress')}
                                placeholder={t('endAddressPlaceholder')}
                                value={endAddress}
                                onChange={(value) =>
                                    handleAddressChange('end', value)
                                }
                                useGoogleMaps={true}
                            />
                        </div>

                        {/* OR Separator */}
                        <div
                            style={{
                                textAlign: 'center',
                                margin: '20px 0',
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '0',
                                    right: '0',
                                    height: '1px',
                                    backgroundColor: '#d9d9d9',
                                    zIndex: 1
                                }}
                            ></div>
                            <span
                                style={{
                                    backgroundColor: '#fff',
                                    padding: '0 15px',
                                    color: '#666',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    zIndex: 2,
                                    position: 'relative'
                                }}
                            >
                                {t('orEnterDistanceManually')}
                            </span>
                        </div>

                        <div className="calc-form-row">
                            <Space direction="vertical">
                                <label>
                                    {t('distance')}
                                    {distance > 0 && (
                                        <span
                                            style={{
                                                fontSize: '12px',
                                                marginLeft: '8px',
                                                color: isManualDistance
                                                    ? '#1890ff'
                                                    : '#52c41a'
                                            }}
                                        >
                                            {isManualDistance ? (
                                                <>
                                                    <Calculator
                                                        size={12}
                                                        style={{
                                                            marginRight: '4px'
                                                        }}
                                                    />
                                                    {t('manualDistance')}
                                                </>
                                            ) : (
                                                <>
                                                    <MapPin
                                                        size={12}
                                                        style={{
                                                            marginRight: '4px'
                                                        }}
                                                    />
                                                    {t(
                                                        'calculatedFromAddresses'
                                                    )}
                                                </>
                                            )}
                                        </span>
                                    )}
                                </label>
                                <Input
                                    prefix={
                                        isManualDistance ? (
                                            <Calculator
                                                size={20}
                                                weight="bold"
                                            />
                                        ) : (
                                            <Path size={20} weight="bold" />
                                        )
                                    }
                                    addonAfter={selectAfter}
                                    value={distance}
                                    onChange={(e) =>
                                        handleManualDistanceChange(
                                            Number(e.target.value)
                                        )
                                    }
                                    type="number"
                                    placeholder={t('enterDistance')}
                                    style={{
                                        borderColor: isManualDistance
                                            ? '#1890ff'
                                            : undefined
                                    }}
                                />
                            </Space>
                            <Space direction="vertical">
                                <label>{t('fuelType')}</label>
                                <div className="custom-select">
                                    <div className="cus-icon">
                                        <GasPump size={20} weight="bold" />
                                    </div>
                                    <Select
                                        value={fuelType}
                                        onChange={(value) => setFuelType(value)}
                                        placeholder={t('selectFuelType')}
                                        suffixIcon={
                                            <CaretDown
                                                size={24}
                                                color="#454545"
                                                weight="bold"
                                            />
                                        }
                                        popupClassName="converter-options"
                                    >
                                        {fuelData?.fuelPrices?.map(
                                            (fuel: FuelPrice) => (
                                                <Option
                                                    key={fuel._id}
                                                    value={fuel.fuelType}
                                                >
                                                    {fuel.fuelType
                                                        .charAt(0)
                                                        .toUpperCase() +
                                                        fuel.fuelType.slice(1)}
                                                </Option>
                                            )
                                        ) || (
                                            <>
                                                <Option value="petrol">
                                                    {t('petrol')}
                                                </Option>
                                                <Option value="diesel">
                                                    {t('diesel')}
                                                </Option>
                                            </>
                                        )}
                                    </Select>
                                </div>
                            </Space>
                        </div>
                        <div className="calc-form-row">
                            <Space direction="vertical">
                                <label>{t('fuelPrice')}</label>
                                <Input
                                    prefix={<Money weight="bold" size={20} />}
                                    addonAfter={selectAfterFuelPriceUnit}
                                    value={fuelPrice || ''}
                                    onChange={(e) =>
                                        setFuelPrice(Number(e.target.value))
                                    }
                                    type="number"
                                    placeholder={t('enterFuelPrice')}
                                />
                                {fuelData?.fuelPrices &&
                                    fuelData.fuelPrices.length > 0 && (
                                        <small
                                            style={{
                                                color: '#666',
                                                fontSize: '12px'
                                            }}
                                        >
                                            {t('priceUpdatedFromAPI')}
                                        </small>
                                    )}
                            </Space>
                            <Space direction="vertical">
                                <label>{t('fuelEconomy')}</label>
                                <Input
                                    prefix={
                                        <CarSimple weight="bold" size={20} />
                                    }
                                    addonAfter={selectAfterFuelEconomyUnit}
                                    value={fuelEconomy}
                                    onChange={(e) =>
                                        setFuelEconomy(Number(e.target.value))
                                    }
                                    type="number"
                                />
                            </Space>
                        </div>
                    </div>
                    <div className="calc-result">
                        <h6>{t('estimatedDrivingCost')}</h6>
                        {formatCalculatedPrice()}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <GoogleMapsLoader>
            <SetMeta
                title={t('drivingCostCalculatorTitle', {
                    ns: 'meta',
                    Country_name: getCountryNameFromDomain()
                })}
                desc={t('drivingCostCalculatorDescMeta', {
                    ns: 'meta',
                    Country_name: getCountryNameFromDomain()
                })}
                keywords={t('drivingCostCalculatorKeywords', {
                    ns: 'meta',
                    Country_name: getCountryNameFromDomain()
                })}
                properties={[
                    {
                        key: 'og:title',
                        value: t('drivingCostCalculatorTitle', {
                            ns: 'meta',
                            Country_name: getCountryNameFromDomain()
                        })
                    },
                    {
                        key: 'og:description',
                        value: t('drivingCostCalculatorDescMeta', {
                            ns: 'meta',
                            Country_name: getCountryNameFromDomain()
                        })
                    },
                    {
                        key: 'og:keywords',
                        value: t('drivingCostCalculatorKeywords', {
                            ns: 'meta',
                            Country_name: getCountryNameFromDomain()
                        })
                    }
                ]}
            />
            <Layout showWhiteNavbar>
                <TrackerLayout
                    effectiveDate={''}
                    tableData={[]}
                    tableHeading={''}
                    coverImage="/images/hero/driving-cost-calc.webp"
                    title={t('drivingCostCalculator')}
                    description={t('drivingCostCalculatorDesc', {
                        Country_name: getCitizenDemonym()
                    })}
                    content={contentData()}
                    converterNode={converterNode()}
                />
            </Layout>
        </GoogleMapsLoader>
    );
};
export default DrivingCostCalculator;
